import React, { useEffect, useMemo, useState } from "react";
import { estimateVACompensation, estimateHigh3Retirement, money, getEvidenceGaps } from "@/lib/claimUtils";
import { VIEWS } from "@/lib/navConfig";
import { computeReadinessSnapshot } from "@/lib/readinessSnapshot";
import { listPrescriptions } from "@/lib/api";
import {
  Crosshair, ShieldStar, Warning, ArrowRight, Target, Lightning,
  CurrencyDollar, Wallet, CheckCircle, ArrowSquareOut, FileText,
  ClipboardText, Calculator, Stack, Lock, MagicWand, ArrowUpRight,
} from "@phosphor-icons/react";

const REFERENCE_ITEMS = [
  { id: "rates", name: "2026 VA Compensation Rates", source: "VA.gov", url: "https://www.va.gov/disability/compensation-rates/veteran-rates/", note: "Effective Dec 1, 2025 · 2.81% COLA" },
  { id: "cfr4", name: "38 CFR Part 4 — Rating Schedule", source: "eCFR.gov", url: "https://www.ecfr.gov/current/title-38/chapter-I/part-4", note: "Schedule for Rating Disabilities" },
  { id: "526ez", name: "VA Form 21-526EZ", source: "VA.gov", url: "https://www.va.gov/forms/21-526ez/", note: "Jan 2026 revision" },
  { id: "10210", name: "VA Form 21-10210", source: "VA.gov", url: "https://www.va.gov/find-forms/about-form-21-10210/", note: "Lay/Witness Statement" },
  { id: "4138", name: "VA Form 21-4138", source: "VA.gov", url: "https://www.va.gov/find-forms/about-form-21-4138/", note: "Statement in Support of Claim" },
  { id: "dbq", name: "Public DBQ Library", source: "VA.gov", url: "https://www.benefits.va.gov/compensation/dbq_publicdbqs.asp", note: "Disability Benefits Questionnaires" },
  { id: "425", name: "38 CFR 4.25 — Combined Ratings Table", source: "eCFR.gov", url: "https://www.ecfr.gov/current/title-38/chapter-I/part-4/subpart-A/section-4.25", note: "VA math progressive combination" },
  { id: "426", name: "38 CFR 4.26 — Bilateral Factor", source: "eCFR.gov", url: "https://www.ecfr.gov/current/title-38/chapter-I/part-4/subpart-A/section-4.26", note: "10% bilateral factor" },
  { id: "0781", name: "VA Form 21-0781", source: "VA.gov", url: "https://www.va.gov/forms/21-0781/", note: "PTSD / MST evidence statement" },
];

const STALE_DAYS = 180;

function scoreLabel(total) {
  if (total >= 85) return "Submission-Ready";
  if (total >= 60) return "Almost Ready";
  if (total >= 35) return "Building";
  return "Just Started";
}

function scoreBandClass(total) {
  if (total >= 85) return "score--green";
  if (total >= 60) return "score--amber";
  return "score--red";
}

function pluralN(n, singular, plural) {
  return n === 1 ? `${n} ${singular}` : `${n} ${plural || singular + "s"}`;
}

function daysAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (24 * 3600 * 1000));
}

const PACKET_ITEMS = [
  { id: "master-conditions", label: "Master condition list" },
  { id: "evidence-index", label: "Evidence index" },
  { id: "526ez-blocks", label: "21-526EZ claim blocks" },
  { id: "stmt-tracker", label: "Personal statements" },
  { id: "lay-stmt", label: "Lay / witness statement tracker" },
  { id: "appt-log", label: "Appointment log" },
  { id: "symptom-history", label: "Symptom history" },
  { id: "med-evidence", label: "Medication evidence" },
  { id: "va-ref", label: "VA source reference page" },
];

export default function DashboardView({
  workspace, profile, readiness, combined, setActiveView,
  issues = [], evidence = [], financials, master526ez, statements = [], isGuest,
}) {
  // ---- Derived metrics (pure) ----
  const snapshot = useMemo(
    () => computeReadinessSnapshot({ profile, master526ez, issues, evidence, statements }),
    [profile, master526ez, issues, evidence, statements],
  );
  const va = estimateVACompensation(combined.rounded, profile.maritalStatus, profile.minorDependents);
  const retired = estimateHigh3Retirement(financials?.high3Average, financials?.yearsService, financials?.monthsService);

  const condTotal = issues.length;
  const condCovered = useMemo(
    () => issues.filter((i) => evidence.some((ev) => (ev.issueIds || []).includes(i.id))).length,
    [issues, evidence],
  );
  const stmtCovered = useMemo(
    () => issues.filter((i) => statements.some((s) => s.issueId === i.id || s.linkedIssueId === i.id)).length,
    [issues, statements],
  );
  const staleEvidence = useMemo(
    () => evidence.filter((e) => {
      const d = daysAgo(e.date || e.dateAdded || e.createdAt);
      return d != null && d > STALE_DAYS;
    }),
    [evidence],
  );

  // Prescriptions for the "refill due" attention item — lazy fetch, never fatal.
  const [rxAlerts, setRxAlerts] = useState([]);
  useEffect(() => {
    if (isGuest) return;
    let cancelled = false;
    listPrescriptions().then((rx) => {
      if (cancelled) return;
      const alerts = (rx || []).map((p) => {
        const filled = p.dateFilled || p.lastFilled;
        const days = filled ? daysAgo(filled) : null;
        const supply = Number(p.daysSupply || 0);
        if (days != null && supply > 0 && days >= supply - 3) {
          return { id: p.id, name: p.medName || p.name || "Medication", days, supply };
        }
        if (p.verificationMismatch) {
          return { id: p.id, name: p.medName || p.name || "Medication", reason: "needs review" };
        }
        return null;
      }).filter(Boolean);
      setRxAlerts(alerts);
    }).catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [isGuest]);

  // ---- Attention items list (drives Hero count + Before-You-Submit card) ----
  const attentionItems = useMemo(() => {
    const items = [];
    rxAlerts.slice(0, 2).forEach((rx) => {
      items.push({
        id: `rx-${rx.id}`,
        title: rx.reason === "needs review"
          ? `${rx.name} — needs review`
          : `${rx.name} refill — ${rx.days} day${rx.days === 1 ? "" : "s"} ago`,
        severity: "warn",
        view: VIEWS.prescriptions,
      });
    });
    if (staleEvidence.length > 0) {
      items.push({
        id: "stale-evidence",
        title: `${staleEvidence.length} evidence item${staleEvidence.length === 1 ? "" : "s"} older than 6 months`,
        severity: "warn",
        view: VIEWS.evidence,
      });
    }
    if (condTotal > 0 && condCovered < condTotal) {
      const missing = condTotal - condCovered;
      items.push({
        id: "evidence-coverage",
        title: `${missing} condition${missing === 1 ? "" : "s"} need evidence attached`,
        severity: "critical",
        view: VIEWS.evidence,
      });
    }
    if (condTotal > 0 && stmtCovered < condTotal) {
      const missing = condTotal - stmtCovered;
      items.push({
        id: "statements-coverage",
        title: `${missing} condition statement${missing === 1 ? "" : "s"} missing`,
        severity: "critical",
        view: VIEWS.statements,
      });
    }
    return items;
  }, [rxAlerts, staleEvidence, condTotal, condCovered, stmtCovered]);

  const attentionCount = attentionItems.length;

  // ---- Next Best Action — the highest-leverage gap we can recommend ----
  const nextBestAction = useMemo(() => {
    if (condTotal === 0) {
      return {
        title: "Add your first condition",
        desc: "Every VA claim starts with a list of conditions you want rated. Add the conditions you're filing for so ClaimOps can build the rest of the packet around them.",
        primaryLabel: "Add Conditions",
        primaryView: VIEWS.conditions,
        secondaryLabel: "View Workflow",
        secondaryView: VIEWS.forms,
      };
    }
    if (stmtCovered < condTotal) {
      const missing = condTotal - stmtCovered;
      return {
        title: `Write statements for ${missing === condTotal ? `your ${condTotal} claimed condition${condTotal === 1 ? "" : "s"}` : `${missing} remaining condition${missing === 1 ? "" : "s"}`}`,
        desc: "Personal statements help explain onset, symptoms, frequency, severity, and functional impact in your own words — they're the single highest-leverage piece of evidence on a BDD claim.",
        primaryLabel: "Start Statements",
        primaryView: VIEWS.statements,
        secondaryLabel: "View Checklist",
        secondaryView: VIEWS.conditions,
      };
    }
    if (condCovered < condTotal) {
      const missing = condTotal - condCovered;
      return {
        title: `Attach evidence to ${missing} condition${missing === 1 ? "" : "s"}`,
        desc: "Conditions without evidence have nothing for the rater to weigh. Upload nexus letters, DBQs, or service treatment records and link each one to its condition.",
        primaryLabel: "Upload Evidence",
        primaryView: VIEWS.evidence,
        secondaryLabel: "Review Gaps",
        secondaryView: VIEWS.conditions,
      };
    }
    return {
      title: "Final review before submission",
      desc: "All conditions have at least one statement and one piece of evidence. Run a final review of your Master Forms and packet so nothing slips.",
      primaryLabel: "Open Master Forms",
      primaryView: VIEWS.forms,
      secondaryLabel: "Preview Packet",
      secondaryView: VIEWS.forms,
    };
  }, [condTotal, stmtCovered, condCovered]);

  const score = snapshot.total;
  const scoreClass = scoreBandClass(score);

  const onFixNext = () => {
    if (attentionItems[0]) setActiveView(attentionItems[0].view);
    else setActiveView(nextBestAction.primaryView);
  };

  return (
    <div className="view-grid view-grid--home" data-testid="dashboard-view">
      {/* ============================= 1. HERO ============================= */}
      <section className="home-hero" data-testid="home-hero">
        <div className="home-hero-glow" aria-hidden="true" />
        <div className="home-hero-eyebrow">
          <ShieldStar size={12} weight="fill" /> CLAIM PACKET STATUS
        </div>
        <h1 className="home-hero-title" data-testid="home-hero-title">
          Your VA claim is <span className="home-hero-pct mono">{score}%</span> packet-ready
        </h1>
        <div className="home-hero-sub" data-testid="home-hero-attention">
          {attentionCount > 0
            ? `${pluralN(attentionCount, "item")} need${attentionCount === 1 ? "s" : ""} attention before submission.`
            : "Nothing flagged for attention right now — keep your evidence fresh."}
        </div>
        <p className="home-hero-desc">
          ClaimOps found missing statements, stale evidence, and condition gaps across your{" "}
          {workspace?.claimType || "BDD"} Claim Workspace.
        </p>
        <div className="home-hero-actions">
          <button className="btn btn--accent home-hero-cta" onClick={onFixNext} data-testid="hero-fix-claim-btn">
            <MagicWand size={14} weight="bold" /> Fix My Claim Packet
          </button>
          <button className="btn btn--ghost-light home-hero-cta-secondary" onClick={() => setActiveView(VIEWS.forms)} data-testid="hero-export-evidence-btn">
            <FileText size={14} weight="bold" /> Export Evidence Index
          </button>
        </div>
        <div className="home-hero-trust">
          <CheckCircle size={12} weight="fill" /> VA forms, rating sources, and 2026 compensation rates verified.
        </div>
      </section>

      {/* ============================= 2. SCORE ============================= */}
      <section className={`home-card home-score-card ${scoreClass}`} data-testid="home-score-card">
        <div className="home-card-eyebrow">
          <Target size={11} weight="fill" /> CLAIM READINESS SCORE
        </div>
        <div className="home-score-figure">
          <div className="home-score-num mono" data-testid="home-score-num">{score}<span className="home-score-pct">%</span></div>
          <div className="home-score-status" data-testid="home-score-status">{scoreLabel(score)}</div>
        </div>
        <div className="home-score-bar" aria-hidden="true">
          <div className="home-score-bar-fill" style={{ width: `${score}%` }} />
        </div>
        <ul className="home-score-points" data-testid="home-score-points">
          <li>
            <span className="home-point-icon home-point-icon--neutral"><Stack size={11} weight="bold" /></span>
            {condCovered} of {condTotal} conditions have evidence attached
          </li>
          <li>
            <span className={`home-point-icon ${stmtCovered === 0 && condTotal > 0 ? "home-point-icon--warn" : "home-point-icon--neutral"}`}>
              <ClipboardText size={11} weight="bold" />
            </span>
            {stmtCovered} of {condTotal} personal statements written
          </li>
          <li>
            <span className={`home-point-icon ${staleEvidence.length > 0 ? "home-point-icon--warn" : "home-point-icon--ok"}`}>
              <Warning size={11} weight="bold" />
            </span>
            {staleEvidence.length} evidence item{staleEvidence.length === 1 ? "" : "s"} older than 6 months
          </li>
          <li>
            <span className={`home-point-icon ${rxAlerts.length > 0 ? "home-point-icon--warn" : "home-point-icon--ok"}`}>
              <Lightning size={11} weight="bold" />
            </span>
            {rxAlerts.length} medication item{rxAlerts.length === 1 ? "" : "s"} need{rxAlerts.length === 1 ? "s" : ""} review
          </li>
        </ul>
        <button className="btn btn--accent home-card-cta" onClick={onFixNext} data-testid="home-score-cta">
          Start Highest-Impact Fix <ArrowRight size={13} weight="bold" />
        </button>
      </section>

      {/* ============================= 3. NEXT BEST ACTION ============================= */}
      <section className="home-card home-nba-card" data-testid="home-nba-card">
        <div className="home-card-eyebrow"><Lightning size={11} weight="fill" /> NEXT BEST ACTION</div>
        <h3 className="home-nba-title" data-testid="home-nba-title">{nextBestAction.title}</h3>
        <p className="home-nba-desc">{nextBestAction.desc}</p>
        <div className="home-card-actions">
          <button className="btn btn--accent" onClick={() => setActiveView(nextBestAction.primaryView)} data-testid="home-nba-primary">
            {nextBestAction.primaryLabel}
          </button>
          <button className="btn btn--outline" onClick={() => setActiveView(nextBestAction.secondaryView)} data-testid="home-nba-secondary">
            {nextBestAction.secondaryLabel}
          </button>
        </div>
      </section>

      {/* ============================= 4. BEFORE YOU SUBMIT ============================= */}
      <section className="home-card home-attention-card" data-testid="home-attention-card">
        <div className="home-card-head">
          <div>
            <div className="home-card-eyebrow"><Warning size={11} weight="fill" /> BEFORE YOU SUBMIT</div>
            <h3 className="home-card-title">
              {attentionCount > 0
                ? `${pluralN(attentionCount, "item")} need${attentionCount === 1 ? "s" : ""} attention`
                : "Everything looks clean"}
            </h3>
          </div>
          {attentionCount > 0 && (
            <button className="btn btn--outline home-card-head-cta" onClick={onFixNext} data-testid="home-attention-review-btn">
              Review Issues <ArrowRight size={12} weight="bold" />
            </button>
          )}
        </div>
        {attentionItems.length === 0 ? (
          <div className="home-attention-empty">
            <CheckCircle size={20} weight="duotone" />
            <span>No blockers. You&apos;re free to print and submit.</span>
          </div>
        ) : (
          <ol className="home-attention-list" data-testid="home-attention-list">
            {attentionItems.map((a, idx) => (
              <li
                key={a.id}
                className={`home-attention-item home-attention-item--${a.severity}`}
                data-testid={`home-attention-item-${a.id}`}
                onClick={() => setActiveView(a.view)}
                onKeyDown={(e) => { if (e.key === "Enter") setActiveView(a.view); }}
                role="button"
                tabIndex={0}
              >
                <span className="home-attention-num mono">{idx + 1}</span>
                <span className="home-attention-label">{a.title}</span>
                <ArrowUpRight size={13} weight="bold" className="home-attention-arrow" />
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* ============================= 5. CLAIM SNAPSHOT ============================= */}
      <section className="home-snapshot" data-testid="home-snapshot">
        <div className="home-card-eyebrow"><Crosshair size={11} weight="fill" /> CLAIM SNAPSHOT</div>
        <div className="home-snapshot-grid">
          <Snap n={condTotal} label="Claimed Conditions" testId="snap-conditions" />
          <Snap n={evidence.length} label="Evidence Items" testId="snap-evidence" />
          <Snap n={`${condCovered} / ${condTotal || 0}`} label="Conditions Covered" testId="snap-covered" tone={condTotal > 0 && condCovered < condTotal ? "warn" : "ok"} />
          <Snap n={`${stmtCovered} / ${condTotal || 0}`} label="Statements Written" testId="snap-statements" tone={condTotal > 0 && stmtCovered < condTotal ? "warn" : "ok"} />
          <Snap n={combined.bilateralApplied ? "YES" : "NO"} label="Bilateral Factor" testId="snap-bilateral" tone={combined.bilateralApplied ? "ok" : "neutral"} />
          <Snap n={`${combined.raw}%`} label="Raw Combined Estimate" testId="snap-raw" />
        </div>
      </section>

      {/* ============================= 6. MONTHLY ESTIMATE ============================= */}
      <section className="home-card home-estimate-card" data-testid="home-estimate-card">
        <div className="home-card-eyebrow"><CurrencyDollar size={11} weight="fill" /> MONTHLY ESTIMATE</div>
        <div className="home-estimate-figure">
          <div className="home-estimate-primary mono" data-testid="home-estimate-monthly">{money(va.monthly + retired.monthly)}</div>
          <div className="home-estimate-subtitle">Combined monthly estimate</div>
          <div className="home-estimate-yearly mono" data-testid="home-estimate-yearly">{money(va.yearly + retired.yearly)} <span className="home-estimate-yearly-suffix">/ year</span></div>
        </div>
        <div className="home-estimate-sub">
          <div className="home-estimate-sub-row" data-testid="home-estimate-va">
            <div className="home-estimate-sub-icon"><CurrencyDollar size={13} weight="duotone" /></div>
            <div className="home-estimate-sub-text">
              <div className="home-estimate-sub-label">VA disability estimate</div>
              <div className="home-estimate-sub-value mono"><span className="nowrap">{money(va.monthly)}</span> <span className="home-estimate-sub-cadence">/ month</span></div>
            </div>
          </div>
          <div className="home-estimate-sub-row" data-testid="home-estimate-high3">
            <div className="home-estimate-sub-icon"><Wallet size={13} weight="duotone" /></div>
            <div className="home-estimate-sub-text">
              <div className="home-estimate-sub-label">High-3 retired pay estimate</div>
              <div className="home-estimate-sub-value mono"><span className="nowrap">{money(retired.monthly)}</span> <span className="home-estimate-sub-cadence">/ month</span></div>
            </div>
          </div>
        </div>
        <button className="btn btn--outline home-card-cta" onClick={() => setActiveView(VIEWS.financials)} data-testid="home-estimate-view-calc">
          View Calculation <ArrowRight size={12} weight="bold" />
        </button>
        <div className="home-estimate-source">
          <CheckCircle size={11} weight="fill" />
          <div className="home-estimate-source-text">
            <div>2026 rates verified</div>
            <div className="home-estimate-source-sub">Effective Dec 1, 2025 · 2.81% COLA</div>
          </div>
        </div>
      </section>

      {/* ============================= 7. PACKET OUTPUT PREVIEW ============================= */}
      <section className="home-card home-packet-card" data-testid="home-packet-card">
        <div className="home-card-eyebrow"><FileText size={11} weight="fill" /> EXPORT-READY CLAIM PACKET</div>
        <h3 className="home-card-title">Export-Ready Claim Packet</h3>
        <p className="home-packet-subtitle">Built from your conditions, evidence, symptoms, forms, and notes.</p>
        <ul className="home-packet-list">
          {PACKET_ITEMS.map((p) => (
            <li key={p.id} data-testid={`packet-${p.id}`}>
              <CheckCircle size={13} weight="fill" /> {p.label}
            </li>
          ))}
        </ul>
        <button className="btn btn--accent home-card-cta" onClick={() => setActiveView(VIEWS.forms)} data-testid="home-packet-preview-btn">
          Preview Export Packet <ArrowRight size={13} weight="bold" />
        </button>
      </section>

      {/* ============================= 8. VERIFIED VA REFERENCES ============================= */}
      <section className="home-card home-refs-card" data-testid="home-refs-card">
        <div className="home-card-head">
          <div>
            <div className="home-card-eyebrow"><CheckCircle size={11} weight="fill" /> VERIFIED VA REFERENCES</div>
            <h3 className="home-card-title">{REFERENCE_ITEMS.length} / {REFERENCE_ITEMS.length} sources verified</h3>
          </div>
        </div>
        <div className="home-refs-grid">
          {REFERENCE_ITEMS.map((ref) => (
            <a
              key={ref.id}
              href={ref.url}
              target="_blank"
              rel="noopener noreferrer"
              className="home-ref-row"
              data-testid={`home-ref-${ref.id}`}
            >
              <CheckCircle size={13} weight="fill" className="home-ref-check" />
              <div className="home-ref-info">
                <div className="home-ref-name">{ref.name}</div>
                <div className="home-ref-note">{ref.note}</div>
              </div>
              <div className="home-ref-source">
                {ref.source} <ArrowSquareOut size={10} weight="bold" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ============================= STICKY MOBILE CTA ============================= */}
      {attentionCount > 0 && (
        <button
          type="button"
          className="home-sticky-cta"
          onClick={onFixNext}
          data-testid="home-sticky-fix-next"
        >
          <Lightning size={14} weight="fill" />
          <span>Fix Next Item</span>
          <span className="home-sticky-cta-count mono">{attentionCount}</span>
        </button>
      )}
    </div>
  );
}

function Snap({ n, label, testId, tone = "neutral" }) {
  return (
    <div className={`home-snap home-snap--${tone}`} data-testid={testId}>
      <div className="home-snap-num mono">{n}</div>
      <div className="home-snap-label">{label}</div>
    </div>
  );
}
